import { useEffect } from 'react';
import {
  Button,
  Card as MuiCard,
  CardContent,
  Divider,
  IconButton,
  TextField,
  styled,
} from '@mui/material';
import { compact } from 'lodash';
import { OnboardingCustomRequestType } from 'further-types/lp/onboarding-custom-request';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import RadioButtons from 'further-ui/components/RadioButtons';
import Switch from 'further-ui/components/Switch';
import {
  getValidFileTypesForUploadType,
  getMaxSizeForUploadType,
} from 'further-ui/config';
import { Controller, useForm } from 'react-hook-form';
import { onboardingCustomRequestTypeLabels } from 'further-ui/labels';
import { zodResolver } from '@hookform/resolvers/zod';
import { Close } from '@mui/icons-material';
import { onboardingCustomRequest } from 'further-ui/validations';
import FileDrop from 'further-ui/components/FileDrop';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import { UploadType } from 'further-types/files';
import { DeepPartial } from 'further-types/utils';
import CkEditor from 'components/CkEditor';

const Card = styled(MuiCard)({
  position: 'relative',
});

const Content = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(3),
  top: theme.spacing(3),
}));

const customRequestTypeOptions = Object.values(OnboardingCustomRequestType)
  .filter((value) => value !== OnboardingCustomRequestType.SectionTitle)
  .map((value) => ({
    label: onboardingCustomRequestTypeLabels[value],
    value,
  }));

export type CustomRequest = {
  title: string;
  description: string;
  type: OnboardingCustomRequestType;
  isRequired: boolean;
  documents: Array<{
    name: string;
    path: string;
  }>;
};

type Props = {
  customRequest?: DeepPartial<CustomRequest> & {
    id?: string;
  };
  onSave: (customRequest: CustomRequest) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  setDraftCustomRequest: (request?: DeepPartial<CustomRequest>) => void;
};

const RequestForm: React.FC<Props> = ({
  customRequest,
  onSave,
  onClose,
  isSubmitting,
  setDraftCustomRequest,
}) => {
  const isNewRequest = !customRequest?.id;
  const { loading: isUploading, uploadFile } = useFileUpload();
  const { error: showError } = useNotification();

  const { control, handleSubmit, watch } = useForm<CustomRequest>({
    defaultValues: customRequest ?? {
      id: undefined,
      title: '',
      description: '',
      type: OnboardingCustomRequestType.Text,
      isRequired: false,
      documents: [],
    },
    resolver: zodResolver(
      onboardingCustomRequest.OnboardingCustomRequestSchema,
    ),
  });

  useEffect(() => {
    if (!isNewRequest) return;

    const subscription = watch((value) => {
      setDraftCustomRequest(value);
    });

    return subscription.unsubscribe;
  }, [isNewRequest, watch, setDraftCustomRequest]);

  return (
    <Card variant="outlined">
      <CloseButton
        size="small"
        onClick={() => {
          setDraftCustomRequest(undefined);
          onClose();
        }}
      >
        <Close fontSize="small" />
      </CloseButton>
      <Content>
        <Column spacing="lg" fullWidth>
          <FormField
            label="Request title"
            tooltip="Provide a title for this document or information request."
          >
            <Controller
              control={control}
              name="title"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter request title"
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField
            label="Request text"
            tooltip="Provide context on the information or document(s) you want the user to provide and why. You can leave this blank if you do not wish to provide further context beyond the title."
          >
            <Controller
              control={control}
              name="description"
              render={({ field, formState: { isSubmitting } }) => (
                <CkEditor
                  {...field}
                  placeholder="Enter request text"
                  disabled={isSubmitting}
                  extraLines={2}
                />
              )}
            />
          </FormField>
          <FormField label="Documents">
            <Controller
              control={control}
              name="documents"
              render={({ field: { value, onChange } }) => (
                <FileDrop
                  files={value.map((file) => ({
                    fileName: file.name,
                    previewUri: file.path,
                    isUploading,
                  }))}
                  onDeleteFile={(fileIndex) => {
                    onChange(value.filter((_, index) => index !== fileIndex));
                  }}
                  onDropFile={async (files) => {
                    const handleFileUpload = async (file: File) => {
                      const { data, error } = await uploadFile(
                        file,
                        UploadType.OnboardingCustomRequestDocuments,
                      );

                      if (error) {
                        showError(error.message);
                        return null;
                      }

                      return {
                        name: data.name,
                        path: data.filePath,
                      };
                    };

                    onChange([
                      ...value,
                      ...files.map((file) => ({
                        name: file.fileName,
                        path: '',
                      })),
                    ]);

                    const uploadedFiles = compact(
                      await Promise.all(
                        files.map(
                          async (droppedFile) =>
                            await handleFileUpload(droppedFile.fileData),
                        ),
                      ),
                    );

                    onChange([...value, ...uploadedFiles]);
                  }}
                  sizeRestriction={{
                    unit: 'mb',
                    value: getMaxSizeForUploadType(
                      UploadType.OnboardingCustomRequestDocuments,
                      'mb',
                    ),
                  }}
                  supportedFormats={getValidFileTypesForUploadType(
                    UploadType.OnboardingCustomRequestDocuments,
                  )}
                />
              )}
            />
          </FormField>
          <FormField label="Investor response type">
            <Controller
              control={control}
              name="type"
              render={({ field, fieldState: { error } }) => (
                <RadioButtons
                  options={customRequestTypeOptions}
                  direction="row"
                  value={field.value}
                  onChange={field.onChange}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <Divider />
          <Row fullWidth centered justify="space-between">
            <Controller
              control={control}
              name="isRequired"
              render={({ field }) => (
                <FormControlLabel
                  slotProps={{
                    typography: {
                      variant: 'h6',
                    },
                  }}
                  labelPlacement="start"
                  label="Required field"
                  control={
                    <Switch checked={field.value} onChange={field.onChange} />
                  }
                />
              )}
            />

            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={handleSubmit((values) => {
                setDraftCustomRequest(undefined);
                onSave(values);
              })}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Row>
        </Column>
      </Content>
    </Card>
  );
};

export default RequestForm;
